import React, { useEffect, useState, useRef } from "react";
import { Carousel } from "@ark-ui/react/carousel";
import { featuredAttractions } from "@/data/attractions";
import { useIsMobile } from "@/hooks/use-mobile";
import { useLanguage } from "@/contexts/LanguageContext";
import { OptimizedImage } from "@/components/OptimizedImage";
import { getHomeBlurPlaceholder } from "@/utils/homeBlurPlaceholders";

const InteractiveSelector = () => {
  const [currentPage, setCurrentPage] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const thumbnailContainerRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  // Safe hook usage with fallback
  let t: (key: string) => string;
  try {
    const { t: useLanguageT } = useLanguage();
    t = useLanguageT;
  } catch (error) {
    // Fallback to English translations if context is not available
    t = (key: string) => {
      const fallbackTranslations: Record<string, string> = {
        "attractions.title": "Discover the Bernese Oberland",
        "attractions.description":
          "Explore Switzerland's most popular attractions, ranked by visitor popularity. From the iconic Jungfraujoch to hidden Alpine gems.",
        "attractions.niederhorn.name": "Niederhorn Peak",
        "attractions.niederhorn.shortDescription":
          "Panoramic views of Lake Thun and the Bernese Alps",
        "attractions.niederhorn.distance": "15 min drive",
        "attractions.niederhorn.duration": "2-4 hours exploration",
        "attractions.st-beatus-caves.name": "St. Beatus Caves",
        "attractions.st-beatus-caves.shortDescription":
          "Largest cave system in Switzerland with underground lakes",
        "attractions.st-beatus-caves.distance": "25 min drive",
        "attractions.st-beatus-caves.duration": "1-2 hours cave tour",
        "attractions.harder-kulm.name": "Harder Kulm",
        "attractions.harder-kulm.shortDescription":
          "Iconic viewpoint overlooking Interlaken and the Jungfrau region",
        "attractions.harder-kulm.distance": "20 min drive",
        "attractions.harder-kulm.duration": "2-3 hours visit",
        "attractions.jungfraujoch.name": "Jungfraujoch - Top of Europe",
        "attractions.jungfraujoch.shortDescription":
          "Europe's highest railway station with glacier views",
        "attractions.jungfraujoch.distance": "45 min drive",
        "attractions.jungfraujoch.duration": "Half day excursion",
        "attractions.thun-castle.name": "Thun Castle",
        "attractions.thun-castle.shortDescription":
          "Medieval castle with panoramic views over Lake Thun",
        "attractions.thun-castle.distance": "30 min drive",
        "attractions.thun-castle.duration": "1-2 hours visit",
        "attractions.spiez-castle.name": "Spiez Castle",
        "attractions.spiez-castle.shortDescription":
          "Historic lakeside castle with beautiful gardens",
        "attractions.spiez-castle.distance": "35 min drive",
        "attractions.spiez-castle.duration": "1-2 hours visit",
        "attractions.schynige-platte.name": "Schynige Platte",
        "attractions.schynige-platte.shortDescription":
          "Historic cogwheel railway to Alpine garden",
        "attractions.schynige-platte.distance": "25 min drive",
        "attractions.schynige-platte.duration": "3-4 hours excursion",
        "attractions.trummelebach-falls.name": "Trümmelbach Falls",
        "attractions.trummelebach-falls.shortDescription":
          "UNESCO World Heritage waterfalls inside the mountain",
        "attractions.trummelebach-falls.distance": "30 min drive",
        "attractions.trummelebach-falls.duration": "1-2 hours visit",
        "attractions.giessbach-falls.name": "Giessbach Falls",
        "attractions.giessbach-falls.shortDescription":
          "Spectacular 14-tier waterfall with historic funicular",
        "attractions.giessbach-falls.distance": "40 min drive",
        "attractions.giessbach-falls.duration": "1-2 hours visit",
        "attractions.lake-thun-brienz.name": "Lake Thun & Brienz",
        "attractions.lake-thun-brienz.shortDescription":
          "Crystal-clear Alpine lakes perfect for boat cruises",
        "attractions.lake-thun-brienz.distance": "20 min drive",
        "attractions.lake-thun-brienz.duration": "2-4 hours cruise",
        "attractions.adventure-park-interlaken.name":
          "Adventure Park Interlaken",
        "attractions.adventure-park-interlaken.shortDescription":
          "Family-friendly adventure park with rope courses",
        "attractions.adventure-park-interlaken.distance": "25 min drive",
        "attractions.adventure-park-interlaken.duration": "2-3 hours activity",
        "attractions.paragliding.name": "Paragliding",
        "attractions.paragliding.shortDescription":
          "Soar over the Alps with tandem paragliding flights",
        "attractions.paragliding.distance": "20 min drive",
        "attractions.paragliding.duration": "2-3 hours experience",
        "attractions.cross-country-skiing.name": "Cross-Country Skiing",
        "attractions.cross-country-skiing.shortDescription":
          "Scenic Nordic skiing trails in pristine Alpine setting",
        "attractions.cross-country-skiing.distance": "15 min drive",
        "attractions.cross-country-skiing.duration": "2-4 hours activity",
        "attractions.beatenberg-panorama-trail.name":
          "Beatenberg Panorama Trail",
        "attractions.beatenberg-panorama-trail.shortDescription":
          "Local hiking trail with stunning mountain views",
        "attractions.beatenberg-panorama-trail.distance": "5 min walk",
        "attractions.beatenberg-panorama-trail.duration": "1-2 hours hike",
        "attractions.saxeten-village.name": "Saxeten Village",
        "attractions.saxeten-village.shortDescription":
          "Traditional Swiss mountain village with authentic charm",
        "attractions.saxeten-village.distance": "20 min drive",
        "attractions.saxeten-village.duration": "1-2 hours visit",
      };
      return fallbackTranslations[key] || key;
    };
  }

  // Helper function to get translated attraction content
  const getTranslatedAttraction = (
    attractionId: string,
    field: "name" | "shortDescription" | "distance" | "duration",
  ) => {
    const translationKey = `attractions.${attractionId}.${field}`;
    return t(translationKey);
  };

  // Define popularity ranking based on internet research
  const popularityOrder = [
    "jungfraujoch", // 1. Most popular - "Top of Europe"
    "harder-kulm", // 2. Very popular viewpoint
    "trummelebach-falls", // 3. UNESCO site, very popular
    "st-beatus-caves", // 4. Largest cave system in Switzerland
    "thun-castle", // 5. Iconic medieval castle
    "spiez-castle", // 6. Popular lakeside castle
    "niederhorn", // 7. Popular local peak
    "schynige-platte", // 8. Historic cogwheel train
    "lake-thun-brienz", // 9. Popular lakes
    "giessbach-falls", // 10. Popular waterfalls
    "paragliding", // 11. Popular adventure activity
    "cross-country-skiing", // 12. Popular winter activity
    "adventure-park-interlaken", // 13. Popular family activity
    "beatenberg-panorama-trail", // 14. Local trail
    "saxeten-village", // 15. Hidden gem
  ];

  // Sort attractions by popularity
  const carouselAttractions = popularityOrder
    .map((id) => featuredAttractions.find((attraction) => attraction.id === id))
    .filter(Boolean) as typeof featuredAttractions;

  // Auto-progress carousel every 3 seconds, but only when not hovering and not on mobile
  useEffect(() => {
    // Disable autoplay on mobile devices
    if (isMobile) {
      return;
    }

    const interval = setInterval(() => {
      if (!isHovering) {
        setCurrentPage(
          (prevPage) => (prevPage + 1) % carouselAttractions.length,
        );
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [carouselAttractions.length, isHovering, isMobile]);

  // Auto-scroll thumbnails to keep active one visible
  useEffect(() => {
    if (thumbnailContainerRef.current) {
      const container = thumbnailContainerRef.current;
      const thumbnails = container.querySelectorAll("button");
      const activeThumbnail = thumbnails[currentPage];

      if (activeThumbnail) {
        const containerRect = container.getBoundingClientRect();
        const thumbnailRect = activeThumbnail.getBoundingClientRect();

        // Calculate if thumbnail is not fully visible
        const isBeforeView = thumbnailRect.left < containerRect.left;
        const isAfterView = thumbnailRect.right > containerRect.right;

        if (isBeforeView || isAfterView) {
          // Scroll to center the active thumbnail
          const scrollLeft =
            activeThumbnail.offsetLeft -
            container.offsetWidth / 2 +
            activeThumbnail.offsetWidth / 2;
          container.scrollTo({
            left: scrollLeft,
            behavior: "smooth",
          });
        }
      }
    }
  }, [currentPage]);

  return (
    <div
      className="relative flex flex-col items-center justify-start pt-2 pb-16 min-h-screen bg-[#e7e3da] font-sans text-gray-800"
      data-oid="4wlg4bp"
    >
      {/* White to beige transition at the top */}
      <div
        className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-white to-[#e7e3da] pointer-events-none"
        data-oid="eiqr_9m"
      ></div>
      {/* Beige to white transition at the bottom */}
      <div
        className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-b from-[#e7e3da] to-white pointer-events-none"
        data-oid="b:aa4l0"
      ></div>
      {/* Header Section */}
      <div
        className="w-full max-w-2xl px-6 mt-2 mb-8 text-center"
        data-oid="h_3ob._"
      >
        <h2
          className="font-playfair text-3xl md:text-4xl lg:text-5xl text-gray-900 mb-4 tracking-tight drop-shadow-lg animate-fadeInTop delay-300 uppercase"
          data-oid="616vld9"
        >
          {t("attractions.title")}
        </h2>
        <p
          className="font-lato text-lg text-gray-900 leading-relaxed max-w-xl mx-auto animate-fadeInTop delay-600"
          data-oid="9pd-q8."
        >
          {t("attractions.description")}
        </p>
      </div>

      {/* Carousel Container */}
      <div className="w-full max-w-6xl mx-auto px-4" data-oid="qvsy3l:">
        <Carousel.Root
          page={currentPage}
          onPageChange={(details) => setCurrentPage(details.page)}
          slideCount={carouselAttractions.length}
          className="w-full"
          data-oid="3cdta:e"
        >
          <Carousel.ItemGroup
            className="overflow-hidden rounded-2xl shadow-2xl mb-6"
            data-oid="kjw1u19"
          >
            {carouselAttractions.map((attraction, index) => (
              <Carousel.Item
                key={attraction.id}
                index={index}
                data-oid="uo8o5_7"
              >
                <div className="relative" data-oid="mw6x0jl">
                  <OptimizedImage
                    src={attraction.image}
                    alt={getTranslatedAttraction(attraction.id, "name")}
                    className="w-full h-[500px] md:h-[600px] object-cover"
                    priority={false}
                    fetchPriority="auto"
                    blurDataURL={getHomeBlurPlaceholder(attraction.image)}
                    data-oid="u7:2-.y"
                  />

                  {/* Attraction name overlay on active image */}
                  <div
                    className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-8"
                    data-oid="i6.8n75"
                  >
                    <h3
                      className="text-white text-3xl md:text-4xl font-semibold font-playfair mb-2"
                      data-oid="t74l8nw"
                    >
                      {getTranslatedAttraction(attraction.id, "name")}
                    </h3>
                    <p
                      className="text-white/90 text-base md:text-lg font-lato mb-4 max-w-2xl"
                      data-oid="s0bxaq."
                    >
                      {getTranslatedAttraction(
                        attraction.id,
                        "shortDescription",
                      )}
                    </p>
                    <div className="flex items-center gap-3" data-oid="h_s6to-">
                      <span
                        className="text-white/90 text-sm bg-white/25 px-3 py-2 rounded-full backdrop-blur-sm"
                        data-oid="oel4ceo"
                      >
                        📍 {getTranslatedAttraction(attraction.id, "distance")}
                      </span>
                      <span
                        className="text-white/90 text-sm bg-white/25 px-3 py-2 rounded-full backdrop-blur-sm"
                        data-oid="tx4iefl"
                      >
                        ⏱️ {getTranslatedAttraction(attraction.id, "duration")}
                      </span>
                    </div>
                  </div>
                </div>
              </Carousel.Item>
            ))}
          </Carousel.ItemGroup>

          <div
            className="flex items-center gap-4 md:gap-6 relative z-10"
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
            data-oid="il6daka"
          >
            <Carousel.PrevTrigger
              className="p-3 bg-white/95 hover:bg-white shadow-xl hover:shadow-2xl rounded-xl transition-all duration-300 shrink-0 text-gray-700 hover:text-gray-900 text-xl md:text-2xl font-bold z-20 border border-gray-200"
              data-oid="tk3.j:8"
            >
              ←
            </Carousel.PrevTrigger>

            <div
              ref={thumbnailContainerRef}
              className="flex gap-3 overflow-x-auto scrollbar-hide flex-1 px-2 md:px-4 scroll-smooth"
              data-oid="7aqrnqq"
            >
              {carouselAttractions.map((attraction, index) => (
                <Carousel.Indicator
                  key={attraction.id}
                  index={index}
                  className={`shrink-0 rounded-xl overflow-hidden cursor-pointer transition-all duration-300 relative ${
                    index === currentPage
                      ? "scale-110 shadow-xl"
                      : "hover:scale-105"
                  }`}
                  data-oid="r73owxe"
                >
                  <img
                    src={attraction.image.replace(
                      ".main-1000h.webp",
                      ".thumb-96h.webp",
                    )}
                    alt={`${attraction.name} thumbnail`}
                    className="w-20 h-16 md:w-24 md:h-20 object-cover"
                    data-oid="p16650k"
                  />

                  {/* Semi-transparent overlay for inactive thumbnails */}
                  {index !== currentPage && (
                    <div
                      className="absolute inset-0 bg-black/50 transition-all duration-300"
                      data-oid="nbgy2b-"
                    />
                  )}
                </Carousel.Indicator>
              ))}
            </div>

            <Carousel.NextTrigger
              className="p-3 bg-white/95 hover:bg-white shadow-xl hover:shadow-2xl rounded-xl transition-all duration-300 shrink-0 text-gray-700 hover:text-gray-900 text-xl md:text-2xl font-bold z-20 border border-gray-200"
              data-oid=":owgffp"
            >
              →
            </Carousel.NextTrigger>
          </div>
        </Carousel.Root>
      </div>

      {/* Custom animations */}
      <style data-oid="73h-v93">{`
        @keyframes fadeInFromTop {
          0% {
            opacity: 0;
            transform: translateY(-20px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fadeInTop {
          opacity: 0;
          transform: translateY(-20px);
          animation: fadeInFromTop 0.8s ease-in-out forwards;
        }

        .delay-300 {
          animation-delay: 0.3s;
        }

        .delay-600 {
          animation-delay: 0.6s;
        }

        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scroll-smooth {
          scroll-behavior: smooth;
        }
      `}</style>
    </div>
  );
};

export default InteractiveSelector;
